import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Button, Loader, Modal } from '@insio/ui-toolkit';
import { useTranslation } from 'react-i18next';
import { PurchaseCreditPackDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useCreditPackOffer } from '../hooks/use-credit-pack-offer';
import {
  CreditPackPurchaseErrorKind,
  creditPackPurchaseError,
  type CreditPackPurchaseError
} from '../utils/credit-pack-purchase-error';
import { openCheckout } from '../utils/open-checkout';

interface BuyCreditPackModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The packs the payer may buy now. Buying one opens its Stripe Checkout; the credits arrive with
 * the paid session, never with the return to the app.
 */
export function BuyCreditPackModal({ open, onClose }: BuyCreditPackModalProps) {
  const t = useFeatureTranslation('credits.packs');
  const { i18n } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const { offer, packs, loading, loadingMore, hasMore, loadMore, error, refetch } =
    useCreditPackOffer({ skip: !open });
  const [purchase] = useMutation(PurchaseCreditPackDocument);
  const [buyingPackId, setBuyingPackId] = useState<string | null>(null);
  const [failure, setFailure] = useState<CreditPackPurchaseError | null>(null);
  const showLoader = useDelayedLoading(loading && offer === undefined);

  const handleClose = () => {
    setFailure(null);
    onClose();
  };

  const handleBuy = async (packId: string) => {
    setFailure(null);
    setBuyingPackId(packId);
    try {
      const result = await purchase({ variables: { input: { packId } } });
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (result.error) throw result.error;
      const checkoutUrl = result.data?.purchaseCreditPack.checkoutUrl;
      if (!checkoutUrl) throw new Error('purchaseCreditPack returned no Checkout URL');
      // The button stays busy while the browser leaves for Stripe.
      openCheckout(checkoutUrl);
    } catch (purchaseError) {
      const reason = creditPackPurchaseError(purchaseError);
      setFailure(reason);
      setBuyingPackId(null);
      // The offer changed under the payer: show what it allows now.
      if (reason.kind !== CreditPackPurchaseErrorKind.GENERIC) void refetch();
    }
  };

  const failureText =
    failure === null
      ? null
      : failure.kind === CreditPackPurchaseErrorKind.REFUSED
        ? t(`refusal.${failure.refusal}`)
        : failure.kind === CreditPackPurchaseErrorKind.NOT_OFFERED
          ? t('error.notOffered')
          : t('error.generic');

  const formatNumber = (value: number) => value.toLocaleString(i18n.language);

  return (
    <Modal open={open} onClose={handleClose} size='sm' className='w-[90vw] sm:w-120'>
      <Modal.Header onClose={handleClose} label={t('title')} description={t('description')} />
      <Modal.Content className='flex flex-col gap-md px-lg pb-lg'>
        {failureText && <Modal.Error role='alert'>{failureText}</Modal.Error>}

        {showLoader && <Loader.Section />}

        {offer !== undefined && error === undefined && offer?.refusal && (
          <Modal.Warning>{t(`refusal.${offer.refusal}`)}</Modal.Warning>
        )}

        {error !== undefined && offer === undefined && (
          <Modal.Error role='alert'>{t('loadError')}</Modal.Error>
        )}

        {offer?.purchasable && packs.length === 0 && (
          <p className='text-sm text-foreground-muted'>{t('empty')}</p>
        )}

        {packs.length > 0 && (
          <ul className='flex flex-col divide-y divide-border-base rounded-lg border border-border-base'>
            {packs.map(pack => (
              <li
                key={pack.id}
                className='flex items-center justify-between gap-md px-md py-sm'
                data-testid='credit-pack-row'
              >
                <div className='flex min-w-0 flex-col gap-2xs'>
                  <span className='text-sm font-semibold tabular-nums text-foreground-base'>
                    {t('credits', { count: pack.credits, credits: formatNumber(pack.credits) })}
                  </span>
                  <span className='text-xs tabular-nums text-foreground-muted'>
                    {t('price', {
                      price: formatCurrency(pack.price.toString(), pack.currency)
                    })}
                  </span>
                </div>
                <Button
                  variant='default'
                  outline='default'
                  size='sm'
                  disabled={buyingPackId !== null}
                  onClick={() => void handleBuy(pack.id)}
                >
                  {t('buy')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {loadingMore && <Loader.Section />}
        {hasMore && !loadingMore && (
          <Button variant='ghost' size='sm' className='self-center' onClick={loadMore}>
            {t('loadMore')}
          </Button>
        )}
      </Modal.Content>
    </Modal>
  );
}
